import React, { Fragment, useEffect, useState } from 'react';
import { Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { isEmpty, get } from 'lodash';
import PropTypes from 'prop-types';
import LogInWithRouter from './components/auth/LogIn';
import MultiTenantLogin from './components/auth/MultiTenantLogin';
import LandingWithRouter from './components/Landing';
import ForgotPasswordWithRouter from './components/auth/ForgotPassword';
import IntroWithRouter from './components/Intro';
import RegisterTopCareersFlow from './components/registration/RegisterTopCareersFlow';
import Top25 from './components/registration/Top25';
import ForgotPasswordVerification from './components/auth/ForgotPasswordVerification';
import GameWithRouter from './components/game/MainGame';
import GameLanding from './components/game/GameLanding';
import AdminPage from './components/admin/AdminPage';
import UserDashboard from './components/user-dashboard/Dashboard';
import ActionPlan from './components/ecap/action-plan/ActionPlan';
import ManageUsers from './components/admin/ManageUsers';
import Education from './components/onboarding/education/Education';
import LocationWithRouter from './components/onboarding/Location';
import Terms from './components/onboarding/Terms';
import CareerPath from './components/career-path/CareerPath';
import NavBarParent from './components/ecap/NavBarParent';
import CareerSearch from './components/career-search/CareerSearch';
import DegreeSearch from './components/career-search/DegreeSearch';
import Reporting from './components/AdvisorDashboard/Reporting';
import AdvisorDashboardBase from './components/AdvisorDashboard/AdvisorDashboardBase';
import Notifications from './components/AdvisorDashboard/Notifications';
import Users from './components/AdvisorDashboard/Users';
import TenantsTab from './components/AdvisorDashboard/TenantsTab/TenantsTab';
import CareersDegreesTabs from './components/AdvisorDashboard/CareersDegreesTabs';
import Degrees from './components/AdvisorDashboard/Degrees';
import GameImages from './components/AdvisorDashboard/GameImages';
import CareerCategoriesAndImages from './components/AdvisorDashboard/CareerCategoriesAndImages';
import PageNotFound from './components/utility/PageNotFound';
import UserProfile from './components/AdvisorDashboard/UserProfile';
import PassiveRegistration from './components/registration/PassiveRegistration';
import {
  getPathWithoutTenant,
  handleFireGTM,
} from './components/utility/routeUtil';
import { educationLevels } from './copy/educationLevels';
import { clearRegistrationData, saveRegistrationSource } from './store/actions';

// See https://github.com/ReactTraining/react-router/issues/5785#issuecomment-359427800
function flatten(target, children) {
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Fragment) {
        flatten(target, child.props.children);
      } else {
        target.push(child);
      }
    }
  });
}

function FragmentSupportingSwitch({ children }) {
  const flattenedChildren = [];
  flatten(flattenedChildren, children);
  // eslint-disable-next-line prefer-spread
  return React.createElement.apply(
    React,
    [Switch, null].concat(flattenedChildren)
  );
}

const Routes = ({
  agreedToTerms,
  country,
  userRole,
  userData,
  territory,
  degreeChoices,
  educationLevel,
  isAuthenticated,
  isRogueUser,
  location,
  match,
  tenant,
  userScore,
  userHasRegistered,
  token,
  userCareerResults,
  userIsFullyRegistered,
  registerOptionsValue,
  ...props
}) => {
  const [prevPath, setPrevPath] = useState(null);
  const history = useHistory();
  const hasBeenToDashboard = get(userData, 'user.hasBeenToDashboard');

  const pathWithoutTenant = getPathWithoutTenant(
    tenant.name,
    location.pathname
  );

  const hasPlayedGame =
    (userData.careerGameResults && userData.careerGameResults.length > 0) ||
    !isEmpty(userScore) ||
    (userData.careerGameResults && userData.careerGameResults.length > 0);

  const hasRegistered = userHasRegistered;

  const handleGTMPathChanges = async () => {
    const previous = prevPath || location.pathname;
    await handleFireGTM(tenant.name, '', previous, location.pathname);

    setPrevPath(pathWithoutTenant);
  };

  const hasThreeDegrees =
    !!degreeChoices['1'] && !!degreeChoices['2'] && !!degreeChoices['3'];
  const hasNoDegrees =
    !degreeChoices['1'] && !degreeChoices['2'] && !degreeChoices['3'];

  const hasThreeCareers =
    !isEmpty(userData) &&
    userData.careerGameResults &&
    userData.careerGameResults.some((career) => career.chosenOrder === 1) &&
    userData.careerGameResults.some((career) => career.chosenOrder === 2) &&
    userData.careerGameResults.some((career) => career.chosenOrder === 3);

  const canSeeECAP =
    !isEmpty(userData) &&
    tenant.features.includes('highschool') &&
    userData.user.educationLevel === educationLevels().level1.value &&
    hasThreeDegrees;

  useEffect(() => {
    if (prevPath !== pathWithoutTenant) {
      handleGTMPathChanges();
    }
  }, [location]);

  useEffect(() => {
    let redirect = null;
    // console.log(
    //   '%cRoutes.js',
    //   'font-weight:bold;color:white;background:purple;',
    //   '\npathWithoutTenant',
    //   pathWithoutTenant,
    //   '\nisAuthenticated',
    //   isAuthenticated,
    //   '\ncountry',
    //   country,
    //   '\nterritory',
    //   territory,
    //   '\neducation',
    //   educationLevel,
    //   '\nhasPlayedGame',
    //   hasPlayedGame,
    //   '\nhasRegistered',
    //   !!hasRegistered,
    //   '\nhasThreeCareers',
    //   hasThreeCareers,
    //   '\nhasNoDegrees',
    //   hasNoDegrees,
    //   '\nhasThreeDegrees',
    //   hasThreeDegrees
    // );

    if (
      !isAuthenticated &&
      ['/landing', '/login', '/verify'].includes(pathWithoutTenant)
    ) {
      console.log(
        `%c*** CLEAR REGISTRATION DATA ***`,
        'font-weight:bold;color:purple;'
      );

      props.clearRegistrationData();
      const params = new URLSearchParams(location.search);
      const registrationSource = params.get('reg_source');

      if (registrationSource) {
        props.saveRegistrationSource(registrationSource);
      }
    } else if (isAuthenticated && isRogueUser) {
      redirect = '/multi-login';
    } else if (
      !educationLevel &&
      pathWithoutTenant === '/onboarding/education'
    ) {
      redirect = '/onboarding/education';
    } else if (!isAuthenticated && !educationLevel) {
      redirect = '/landing';
    } else if (!educationLevel) {
      redirect = '/onboarding/education';
    } else if (!country || (country === 'United States' && !territory)) {
      redirect = '/onboarding/location';
    } else if (!agreedToTerms && !hasRegistered) {
      // DEV NOTE: If we were saving this to the db, we could probably get rid of the hasRegistered check
      redirect = '/terms-and-conditions';
    } else if (!hasPlayedGame) {
      redirect = pathWithoutTenant === '/game' ? null : '/game-landing';
    } else if (
      (!hasRegistered && !isAuthenticated) ||
      (!userHasRegistered &&
        isAuthenticated &&
        (registerOptionsValue === 'federation' ||
          registerOptionsValue === 'social'))
    ) {
      redirect = '/registration';
    } else if (
      (!hasThreeCareers || hasNoDegrees) &&
      userIsFullyRegistered &&
      pathWithoutTenant !== '/career-path/path-pairing'
    ) {
      redirect = '/career-path/top-3';
    } else if (
      (hasThreeCareers && !hasThreeDegrees) ||
      (hasThreeDegrees && tenant.name === 'InStride')
    ) {
      redirect = '/career-path/path-pairing';
    } else if (
      hasThreeDegrees &&
      !pathWithoutTenant.includes('/career-search') &&
      !pathWithoutTenant.includes('/degree-search') &&
      !pathWithoutTenant.includes('/advisor-dashboard') &&
      !pathWithoutTenant.includes('/admin') &&
      !pathWithoutTenant.includes('/education-plan') &&
      !pathWithoutTenant.includes('/career-path/path-pairing') &&
      !pathWithoutTenant.includes('/edit-profile') &&
      tenant.name !== 'InStride'
    ) {
      redirect = '/dashboard';
    }

    if (!redirect) {
      // console.log(
      //   `%c>>> NO REDIRECT >>>`,
      //   'font-weight:bold;color:white;background:purple;'
      // );
    } else if (redirect === pathWithoutTenant) {
      // console.log(
      //   `%c>>> STAY ON PAGE ${match.path}${redirect} >>>`,
      //   'font-weight:bold;color:white;background:purple;'
      // );
    } else {
      // console.log(
      //   `%c>>> REDIRECT TO ${match.path}${redirect} >>>`,
      //   'font-weight:bold;color:white;background:purple;'
      // );

      history.replace(`${match.path}${redirect}`);
    }
  }, [isAuthenticated, userIsFullyRegistered]);

  const canSeeAdvisorDash = () => {
    if (!userRole) return false;
    return (
      userRole === 'School Advisor' ||
      userRole === 'School Admin' ||
      userRole === 'District Advisor' ||
      userRole === 'District Admin' ||
      userRole === 'Tenant Admin' ||
      userRole === 'Super User'
    );
  };

  const canSeeTenantsTab = () => {
    if (!userRole) return false;
    return userRole === 'Tenant Admin' || userRole === 'Super User';
  };

  const advisorDashboardStyling = {
    mainContent: {
      margin: '44px 55px 0 55px',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    boxWithBoxShadow: {
      backgroundColor: 'white',
      boxShadow: '3px 3px 15px #00000015',
      border: '1px solid #E8E8E8',
      borderRadius: '7px',
    },
    boxWithSecondaryUnderline: {
      backgroundColor: 'white',
      border: '1px solid rgba(112,112,112, .5)',
      borderRadius: '7px',
      borderBottom: `5px solid ${tenant.secondary}`,
    },
    boxWithShadowAndUnderline: {
      backgroundColor: 'white',
      boxShadow: '3px 3px 15px #00000015',
      border: '1px solid #E8E8E8',
      borderRadius: '7px',
      borderBottom: `5px solid ${tenant.secondary}`,
    },
  };

  const getNoAuthRoutes = () => (
    <>
      <Route
        exact
        path={`${match.path}/landing`}
        render={() => <LandingWithRouter tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/login`}
        render={() => <LogInWithRouter tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/password-reset`}
        render={() => <ForgotPasswordWithRouter tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/verify`}
        component={() => <ForgotPasswordVerification />}
      />
      <Route
        exact
        path={`${match.path}/introduction`}
        render={() => <IntroWithRouter tenant={tenant} />}
      />
    </>
  );

  const getAdvisorDashRoutes = () => {
    if (pathWithoutTenant === '/advisor-dashboard') {
      return (
        <Redirect
          from={`/${tenant.name}/advisor-dashboard`}
          to={`/${tenant.name.toLowerCase()}/advisor-dashboard/reporting`}
        />
      );
    }
    if (
      pathWithoutTenant === '/advisor-dashboard/tenants' &&
      !canSeeTenantsTab()
    ) {
      return (
        <Redirect
          from={`/${tenant.name}/advisor-dashboard`}
          to={`/${tenant.name.toLowerCase()}/advisor-dashboard/reporting`}
        />
      );
    }
    return (
      <>
        <Route
          exact
          path={`${match.path}/admin`}
          component={() => <AdminPage />}
        />
        <Route
          exact
          path={`${match.path}/admin/manage-users`}
          component={() => <ManageUsers />}
        />
        <Route
          path={`${match.path}/advisor-dashboard/user-profile`}
          render={() => (
            <UserProfile tenant={tenant} styling={advisorDashboardStyling} />
          )}
        />
        <Route
          path={`${match.path}/advisor-dashboard`}
          render={() => (
            <>
              <AdvisorDashboardBase tenant={tenant} />
              <Route
                path={`${match.path}/advisor-dashboard/reporting`}
                render={() => (
                  <Reporting
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/notifications`}
                render={() => (
                  <Notifications
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/users`}
                render={() => (
                  <Users tenant={tenant} styling={advisorDashboardStyling} />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/tenants`}
                render={() => (
                  <TenantsTab
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/careers`}
                render={() => (
                  <CareersDegreesTabs
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                    data="careers"
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/degrees`}
                render={() => (
                  <CareersDegreesTabs
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                    data="degrees"
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/game-images`}
                render={() => (
                  <GameImages
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                  />
                )}
              />
              <Route
                path={`${match.path}/advisor-dashboard/career-categories-and-images`}
                render={() => (
                  <CareerCategoriesAndImages
                    tenant={tenant}
                    styling={advisorDashboardStyling}
                  />
                )}
              />
            </>
          )}
        />
      </>
    );
  };

  const getAuthRoutesDefault = () => (
    <>
      <Route
        exact
        path={`${match.path}/landing`}
        render={() => <LandingWithRouter tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/game-landing?retry=true`}
        render={() => <GameLanding tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/career-search`}
        render={() => <CareerSearch tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/degree-search`}
        render={() => <DegreeSearch tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/multi-login`}
        render={() => <MultiTenantLogin tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/dashboard`}
        render={() => <UserDashboard tenant={tenant} endpoint="dashboard" />}
      />
      <Route
        exact
        path={`${match.path}/career-path/top-3`}
        render={() => <Top25 tenant={tenant} />}
      />
      <Route
        exact
        path={`${match.path}/career-path/path-pairing`}
        render={() => <CareerPath tenant={tenant} />}
      />
      {hasBeenToDashboard ? (
        <Route
          exact
          path={`${match.path}/edit-profile`}
          render={() => (
            <UserDashboard tenant={tenant} endpoint="edit-profile" />
          )}
        />
      ) : null}
    </>
  );

  const getAuthRoutesECAP = () => (
    <>
      <Route
        path={`${match.path}/action-plan`}
        render={() => <ActionPlan tenant={tenant} />}
      />
      <Route
        path={`${match.path}/education-plan`}
        render={() => <NavBarParent tenant={tenant} />}
      />
    </>
  );

  return (
    <>
      <FragmentSupportingSwitch>
        {isAuthenticated ? null : getNoAuthRoutes()}
        {/* Registration Flow */}
        <Route
          exact
          path={`${match.path}/onboarding/education`}
          render={() => (
            <Education tenant={tenant} educationLevel={educationLevel} />
          )}
        />
        <Route
          exact
          path={`${match.path}/onboarding/location`}
          render={() => <LocationWithRouter tenant={tenant} />}
        />
        <Route
          exact
          path={`${match.path}/terms-and-conditions`}
          render={() => <Terms tenant={tenant} />}
        />
        <Route
          exact
          path={`${match.path}/game-landing`}
          render={() => <GameLanding tenant={tenant} />}
        />
        <Route
          exact
          path={`${match.path}/game`}
          render={() => <GameWithRouter tenant={tenant} />}
        />
        <Route
          exact
          path={`${match.path}/registration`}
          render={() => {
            if (tenant.name === 'Baobab') {
              return <PassiveRegistration />;
            }
            return <RegisterTopCareersFlow tenant={tenant} />;
          }}
        />

        {/* End Registration Flow */}

        {isAuthenticated && getAuthRoutesDefault()}

        {isAuthenticated && canSeeAdvisorDash() && getAdvisorDashRoutes()}

        {isAuthenticated && canSeeECAP ? getAuthRoutesECAP() : null}

        <Route
          exact
          path={`${match.path}/404`}
          render={() => (
            <PageNotFound
              tenant={tenant}
              isAuthenticated={isAuthenticated}
              hasPairedThree={hasThreeDegrees}
              careersHaveBeenSelected={hasThreeCareers}
              careerGameResults={userData.careerGameResults}
            />
          )}
        />

        <Route
          render={() => {
            // console.log(
            //   '%cFALLBACK',
            //   'font-weight:bold;color:purple;',
            //   isAuthenticated
            // );

            if (isAuthenticated && tenant.name !== 'InStride') {
              return (
                <Redirect to={`/${tenant.name.toLowerCase()}/dashboard`} />
              );
            }

            return (
              <Redirect
                to={{
                  pathname: `/${tenant.name.toLowerCase()}/404`,
                  state: {
                    hasBeenToDashboard,
                  },
                }}
              />
            );
          }}
        />
      </FragmentSupportingSwitch>
    </>
  );
};

Routes.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  tenant: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  degreeChoices: PropTypes.object.isRequired,
  agreedToTerms: PropTypes.bool.isRequired,
  educationLevel: PropTypes.string,
  country: PropTypes.string,
};

export default connect(null, {
  clearRegistrationData,
  saveRegistrationSource,
})(Routes);
